import { Outlet, useRouterState } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { SectionNavSkeleton, SectionPills, SectionTabs } from "@/components/app/SectionNav";
import { useFeatures, visibilityOf, isNavVisible } from "@/lib/license";
import { activeGroupId, visibleGroups, type SettingsItemState } from "./settingsNav";

/**
 * Settings hub ve stylu UUI settings-pages — globální hlavička, horní underline
 * lišta se skupinami a pod ní pill řádek s položkami aktivní skupiny. Struktura
 * i feature gating jsou v {@link ./settingsNav.ts}; podstránky si už vlastní `h1`
 * nedrží (mají `SectionHeader` s `h2`).
 */
export function SettingsLayout() {
  const { t } = useTranslation();
  const features = useFeatures();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const show = (id: string) => isNavVisible(visibilityOf(features.data, id));
  const stateOf = (id: string): SettingsItemState => {
    const v = visibilityOf(features.data, id);
    return v === "LOCKED" ? "locked" : v === "ADVERTISED" ? "advertised" : "enabled";
  };

  const groups = visibleGroups(show, stateOf);
  const currentId = activeGroupId(pathname);
  const currentGroup = groups.find((g) => g.id === currentId) ?? null;

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-display-sm font-semibold text-primary">{t("settings.title")}</h1>
        <p className="mt-1 text-md text-tertiary">{t("settings.subtitle")}</p>
      </div>

      {/* Dokud se nenačtou features, default-deny by odfiltroval gated skupiny
          a lišta by se po načtení viditelně přeskládala → placeholder. */}
      {features.isLoading ? (
        <SectionNavSkeleton />
      ) : (
        <div className="flex flex-col gap-4">
          <SectionTabs
            aria-label={t("settings.title")}
            activeTo={currentGroup?.to ?? null}
            items={groups.map((g) => ({ to: g.to, label: t(g.labelKey) }))}
          />
          {currentGroup && currentGroup.items.length > 0 && (
            <SectionPills
              aria-label={t(currentGroup.labelKey)}
              pathname={pathname}
              items={currentGroup.items.map((i) => ({
                to: i.to,
                label: i.state && i.state !== "enabled" ? `${t(i.labelKey)} · ${t(`feature.${i.state}.badge`)}` : t(i.labelKey),
                icon: i.icon,
              }))}
            />
          )}
        </div>
      )}

      <div className="min-w-0">
        <Outlet />
      </div>
    </div>
  );
}
